import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography } from '@mui/material';

export default function DropzoneField({
    dropzoneText = "Drag and drop a file here or click",
    dropzoneClass = "dropzone dropzone-default min-drop p-2",
    dropzoneParagraphClass = "dropzone-msg-title",
    acceptedFiles = [],
    filesLimit = 1,
    getPreviewIcon,
    showAlerts = [],
    onChange,
    ...props
}) {
    const [files, setFiles] = useState([]);
    const [error, setError] = useState(null);

    const onDrop = useCallback((acceptedFilesList, rejectedFiles) => {
        setError(null);
        
        if (rejectedFiles && rejectedFiles.length > 0) {
            const rejection = rejectedFiles[0];
            if (rejection.errors && rejection.errors.length > 0) {
                setError(rejection.errors[0].message);
            }
        }

        if (acceptedFilesList && acceptedFilesList.length > 0) {
            const limitedFiles = filesLimit > 0 ? acceptedFilesList.slice(0, filesLimit) : acceptedFilesList;
            setFiles(limitedFiles);
            // Pass single file if filesLimit is 1, otherwise pass array
            if (onChange) {
                onChange(filesLimit === 1 ? limitedFiles[0] ? [limitedFiles[0]] : [] : limitedFiles);
            }
        } else {
            // Clear files if nothing accepted
            setFiles([]);
            if (onChange) {
                onChange([]);
            }
        }
    }, [filesLimit, onChange]);

    // Convert acceptedFiles array to react-dropzone accept format (string or string[])
    const acceptValue = React.useMemo(() => {
        if (!Array.isArray(acceptedFiles) || acceptedFiles.length === 0) {
            return undefined;
        }

        // Flatten all MIME types into a single array
        const allTypes = [];
        acceptedFiles.forEach(item => {
            if (typeof item === 'string' && item.trim()) {
                item.split(',').forEach(type => {
                    const trimmed = type.trim();
                    if (trimmed && trimmed.includes('/')) {
                        allTypes.push(trimmed);
                    }
                });
            }
        });

        return allTypes.length > 0 ? allTypes : undefined;
    }, [acceptedFiles]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: acceptValue,
        maxFiles: filesLimit > 0 ? filesLimit : undefined,
        multiple: filesLimit !== 1,
        noClick: false,
        noKeyboard: false
    });

    // Store object URLs for cleanup
    const [objectUrls, setObjectUrls] = useState([]);

    const removeFile = (index) => {
        // Clean up object URL if it exists
        if (objectUrls[index]) {
            URL.revokeObjectURL(objectUrls[index]);
            const newUrls = objectUrls.filter((_, i) => i !== index);
            setObjectUrls(newUrls);
        }
        
        const newFiles = files.filter((_, i) => i !== index);
        setFiles(newFiles);
        if (onChange) {
            onChange(newFiles.length > 0 ? [newFiles[0]] : []);
        }
    };

    // Update object URLs when files change
    React.useEffect(() => {
        // Create new URLs for image files
        const newUrls = files.map(file => {
            if (file && file.type && file.type.startsWith('image/')) {
                return URL.createObjectURL(file);
            }
            return null;
        });

        setObjectUrls(newUrls);

        // Cleanup function
        return () => {
            newUrls.forEach(url => {
                if (url) URL.revokeObjectURL(url);
            });
        };
    }, [files]);

    return (
        <div>
            <Box
                {...getRootProps()}
                className={dropzoneClass}
                sx={{
                    border: '2px dashed',
                    borderColor: error ? 'error.main' : 'primary.main',
                    borderRadius: 1,
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
                    '&:hover': {
                        backgroundColor: 'action.hover'
                    }
                }}
            >
                <input {...getInputProps()} />
                <Typography className={dropzoneParagraphClass} variant="body2">
                    {isDragActive ? 'Drop the file here' : dropzoneText}
                </Typography>
                {error && showAlerts.includes('error') && (
                    <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                        {error}
                    </Typography>
                )}
            </Box>
            {files.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    {files.map((file, index) => {
                        // Wrap file in expected format for getPreviewIcon compatibility
                        const fileObject = {
                            file: file,
                            data: objectUrls[index] || null
                        };
                        
                        return (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                {getPreviewIcon ? (
                                    getPreviewIcon(fileObject, {})
                                ) : (
                                    <Typography variant="body2">{file.name}</Typography>
                                )}
                                <Typography
                                    variant="body2"
                                    color="error"
                                    sx={{ ml: 'auto', cursor: 'pointer' }}
                                    onClick={() => removeFile(index)}
                                >
                                    Remove
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </div>
    );
}

